import type { ServerMessage } from "@screener/protocol";
import { useEffect, useState } from "react";
import { applyMessages, initialStreamState, type StreamState } from "./stream-state";

/**
 * Предел буфера, после которого пачка применяется немедленно, не дожидаясь
 * кадра. Кадров в скрытой вкладке не бывает вовсе — браузер их не выдаёт, —
 * а поток демона не останавливается, и буфер рос бы всё время, пока трейдер
 * смотрит в другую вкладку. Применение целиком ничего не теряет: оно ровно то,
 * что сделал бы кадр, просто раньше.
 */
export const MAX_BUFFERED_MESSAGES = 2_000;

/** Планировщик кадров: в браузере — анимационный кадр, в тестах подменяется. */
export interface FrameScheduler {
  request: (callback: () => void) => number;
  cancel: (handle: number) => void;
}

const BROWSER_SCHEDULER: FrameScheduler = {
  request: (callback) => requestAnimationFrame(callback),
  cancel: (handle) => cancelAnimationFrame(handle),
};

/**
 * Внешнее хранилище состояния потока §7.6. Состояние живёт не в `useState`
 * корня: каждый применённый кадр перерисовывал бы `App` и всё дерево под ним
 * (замер на проде — сайдбар, шапка и фильтры перерисовывались наравне с
 * карточками). Читатели подписываются на нужный им срез через
 * `useStreamSelector`.
 */
export interface StreamStore {
  getSnapshot: () => StreamState;
  subscribe: (listener: () => void) => () => void;
  /** Только буферизует: состояние меняется кадром (или пределом буфера). */
  onMessage: (message: ServerMessage) => void;
  /**
   * Сброс к пустому состоянию. Нужен на выходе из сессии: иначе на экране
   * входа за формой остались бы плотности прежнего пользователя, а после
   * входа другого — вперемешку с его собственными.
   */
  reset: () => void;
  /** Снять отложенный кадр и очистить буфер — при размонтировании. */
  dispose: () => void;
}

/**
 * События копятся в буфере и применяются пачкой на кадр анимации: демон ведёт
 * полный перечень инструментов, и перерисовка на каждое событие означала бы
 * сотни перерисовок в секунду там, где глаз читает десяток. Кадр выбран
 * потому, что он и есть частота, с которой экран физически обновляется —
 * применять чаще некуда, применять реже значит терять свежесть на глазах.
 *
 * Буфер применяется целиком и очищается только после применения, поэтому
 * потерять сообщение он не может; порядок поступления сохраняется.
 */
export function createStreamStore(
  initial: StreamState = initialStreamState,
  scheduler: FrameScheduler = BROWSER_SCHEDULER,
): StreamStore {
  let state = initial;
  let buffer: ServerMessage[] = [];
  let frame: number | undefined;
  const listeners = new Set<() => void>();

  function publish(next: StreamState): void {
    if (next === state) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function flush(): void {
    frame = undefined;
    const batch = buffer;
    if (batch.length === 0) {
      return;
    }
    buffer = [];
    publish(applyMessages(state, batch));
  }

  function cancelFrame(): void {
    if (frame !== undefined) {
      scheduler.cancel(frame);
      frame = undefined;
    }
  }

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    onMessage: (message) => {
      buffer.push(message);
      if (buffer.length >= MAX_BUFFERED_MESSAGES) {
        cancelFrame();
        flush();
        return;
      }
      if (frame === undefined) {
        frame = scheduler.request(flush);
      }
    },
    reset: () => {
      cancelFrame();
      buffer = [];
      publish(initialStreamState);
    },
    dispose: () => {
      cancelFrame();
      buffer = [];
    },
  };
}

/**
 * Хранилище потока на время жизни корня: создаётся один раз, ссылка стабильна.
 * Корень не читает состояние в рендере, а потому и не перерисовывается кадрами.
 */
export function useDensityStream(): StreamStore {
  const [store] = useState(() => createStreamStore());
  useEffect(() => () => store.dispose(), [store]);
  return store;
}
